import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  fetchCognitoUsers,
  fetchFirmsFromFirebase,
  getUploadUrls,
  RootState,
  searchEmail,
  submitOfficials,
} from "../store";
import { CustomCloseButton } from "../components/ToasterCloseButton";
import { toast } from "react-toastify";
import { calendarDayToEpoch } from "../utils/helper";
import { TIME_ZONES } from "../utils/sessionHelper";
import { MARKET_OPTIONS, MARKET_DEFAULT_TIMEZONE } from "../constants/markets";
import Dropdown from "../components/home/Dropdown";
import { SingleValue, ActionMeta } from "react-select";

type UploadFileKeys = "seal" | "signature" | "cert";

const OnboardOfficial = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [formKey, setFormKey] = useState(0);
  const [isLicensedUser, setIsLicensedUser] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { data: users } = useSelector((state: RootState) => state.session.cognitoUsers);
  const { data: firms } = useSelector((state: RootState) => state.session.allFirms);
  const [formData, setFormData] = useState({
    firm: "",
    user: "",
    number: "",
    state: "",
    market: "",
    timezone: "",
    county: "",
    commissionExpirationDate: "",
    seal: null,
    signature: null,
    cert: null,
  });
  const [isOnboarding, setIsOnboarding] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!isLicensedUser && !formData.firm) newErrors.firm = "Firm is required";
    if (!formData.user) newErrors.user = "User is required";
    if (!formData.number) newErrors.number = "Number is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.market) newErrors.market = "Market is required";
    if (!formData.timezone) newErrors.timezone = "Time zone is required";
    if (!formData.county) newErrors.county = "County is required";
    if (!formData.commissionExpirationDate) newErrors.commissionExpirationDate = "Expiration date is required";
    return newErrors;
  };

  useEffect(() => {
    dispatch(fetchCognitoUsers());
    dispatch(fetchFirmsFromFirebase());
  }, [dispatch]);

  const timeZoneOptions = TIME_ZONES.map((zone: any) => ({
    value: zone.value,
    label: zone.name,
  }));

  // Choosing a market pre-fills its usual zone so the common case is one click.
  // It only ever fills a BLANK timezone — never overwrites a zone already chosen,
  // since several markets span two zones and the explicit pick must win.
  const handleMarketChange = (
    newValue: SingleValue<{ value: string; label: string }>,
    _actionMeta: ActionMeta<{ value: string; label: string }>
  ) => {
    if (!newValue) return;
    const market = newValue.value;
    setFormData((prev) => ({
      ...prev,
      market,
      timezone: prev.timezone || MARKET_DEFAULT_TIMEZONE[market] || "",
    }));
  };

  const handleTimezoneChange = (
    newValue: SingleValue<{ value: string; label: string }>,
    _actionMeta: ActionMeta<{ value: string; label: string }>
  ) => {
    if (!newValue) return;
    setFormData((prev) => ({ ...prev, timezone: newValue.value }));
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e: any) => {
    const { name, files } = e.target;
    setFormData({ ...formData, [name]: files[0] });
  };

  const handleSubmit = async (e: any) => {
    try {
    e.preventDefault();
    setIsOnboarding(true);

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsOnboarding(false);
      return;
    }

    setErrors({});
    const selectedUser = users.find((user) => user.Username === formData.user);
    if (!selectedUser) {
      toast.error("User not Found", {
        autoClose: 3000,
        closeButton: <CustomCloseButton />,
        closeOnClick: false,
        icon: () => null,
      });
      return;
    }

    const attributes = Object.fromEntries(selectedUser.Attributes.map((a: any) => [a.Name, a.Value]));
    const searchEmailResponse = await dispatch(
      searchEmail({ email: attributes.email })
    );
    if (searchEmailResponse.meta.requestStatus === "fulfilled") {
      let officialId;
      if (searchEmailResponse.payload.result) {
        officialId = searchEmailResponse.payload.result?.officialId;
      } else {
        officialId = selectedUser.Username;
      }
     
        const result = await dispatch(getUploadUrls({ officialId }));
        if (getUploadUrls.fulfilled.match(result)) {
          const uploadUrls: { type: UploadFileKeys; url: string; contentType: string }[] = result.payload.data;

          if (!uploadUrls || !Array.isArray(uploadUrls)) {
            throw new Error("Invalid upload URLs response");
          }
          for (const file of uploadUrls) {
            const fileToUpload = formData[file.type];
            if (fileToUpload) {
              await fetch(file.url, {
                method: "PUT",
                headers: { "Content-Type": file.contentType },
                body: fileToUpload
              });
            }
          }
          const finalPayload = {
            county: formData.county,
            digitalNumber: formData.number,
            email: attributes.email,
            expirationDate: calendarDayToEpoch(formData.commissionExpirationDate),
            firmId: isLicensedUser ? officialId : formData.firm,
            name: attributes.name || `${attributes.given_name || ""} ${attributes.family_name || ""}`.trim(),
            state: formData.state,
            market: formData.market,
            timezone: formData.timezone,
            uid: officialId,
          };

          const submitOfficialsResponse = await dispatch(submitOfficials({ body: finalPayload }));

          if (submitOfficialsResponse.meta.requestStatus === "fulfilled") {
            toast.success("User onboarded successfully", {
              autoClose: 3000,
              closeButton: <CustomCloseButton />,
              closeOnClick: false,
              icon: () => null,
            });
            setFormData({
              firm: "",
              user: "",
              number: "",
              state: "",
              market: "",
              timezone: "",
              county: "",
              commissionExpirationDate: "",
              seal: null,
              signature: null,
              cert: null,
            });
            setIsLicensedUser(false);
            setFormKey((prev) => prev + 1);
          } else {
            toast.error(submitOfficialsResponse.payload?.error || "Rejected", {
              autoClose: 3000,
              closeButton: <CustomCloseButton />,
              closeOnClick: false,
              icon: () => null,
            });
          }
        }
      } 
      else {
        toast.error(searchEmailResponse?.payload?.error || "Rejected", {
          autoClose: 3000,
          closeButton: <CustomCloseButton />,
          closeOnClick: false,
          icon: () => null,
        });
      }
    }
    catch (error) {
      console.error("Error onboarding official:", error);
      toast.error("Error onboarding official", {
        autoClose: 3000,
        closeButton: <CustomCloseButton />,
        closeOnClick: false,
        icon: () => null,
      });
    } finally {
      setIsOnboarding(false);
    }
  };

  return (
    <PageContainer>
      <SignInBox>
        <Title>Onboard a new official</Title>
        <Form key={formKey}>
          <CheckboxContainer>
            <input
              type="checkbox"
              id="licensedUser"
              checked={isLicensedUser}
              onChange={() => setIsLicensedUser((prev) => !prev)}
            />
            <label htmlFor="licensedUser">Is a licensed user?</label>
          </CheckboxContainer>

          {!isLicensedUser && (
            <>
              <Label>Select Firm *</Label>
              <Select name="firm" onChange={handleInputChange} value={formData.firm} required>
                <option value="">Select Firm</option>
                {firms.map((firm) => (
                  <option key={firm?.firmID} value={firm?.firmID}>
                    {`${firm?.firmName} (${firm?.firmID})`}
                  </option>
                ))}
              </Select>
              {errors.firm && <ErrorText>{errors.firm}</ErrorText>}
            </>
          )}

          <Label>Select User *</Label>
          <Select name="user" onChange={handleInputChange} value={formData.user} required>
            <option value="">Select User</option>
            {users.map((user: any) => {
              const attributes = Object.fromEntries(user.Attributes.map((a: any) => [a.Name, a.Value]));
              return (
                <option key={user.Username} value={user.Username}>
                  {attributes.email || user.Username}
                </option>
              );
            })}
          </Select>
          {errors.user && <ErrorText>{errors.user}</ErrorText>}

          <Label>Number</Label>
          <Input type="text" name="number" onChange={handleInputChange} value={formData.number} />
          {errors.number && <ErrorText>{errors.number}</ErrorText>}

          <Label>State</Label>
          <Input type="text" name="state" onChange={handleInputChange} value={formData.state} />
          {errors.state && <ErrorText>{errors.state}</ErrorText>}

          <Label>Market</Label>
          <Dropdown
            value={
              MARKET_OPTIONS.find((opt) => opt.value === formData.market) || {
                value: "",
                label: "Select Market",
              }
            }
            onChange={handleMarketChange}
            options={MARKET_OPTIONS}
            isSearchable={true}
          />
          {errors.market && <ErrorText>{errors.market}</ErrorText>}

          <Label>Time Zone</Label>
          <Dropdown
            value={
              timeZoneOptions.find((opt: any) => opt.value === formData.timezone) || {
                value: "",
                label: "Select Time Zone",
              }
            }
            onChange={handleTimezoneChange}
            options={timeZoneOptions}
            isSearchable={true}
          />
          {errors.timezone && <ErrorText>{errors.timezone}</ErrorText>}

          <Label>County</Label>
          <Input type="text" name="county" onChange={handleInputChange} value={formData.county} />
          {errors.county && <ErrorText>{errors.county}</ErrorText>}

          <Label>Commission Expiration Date</Label>
          <Input
            type="date"
            name="commissionExpirationDate"
            onChange={handleInputChange}
            value={formData.commissionExpirationDate}
          />
          {errors.commissionExpirationDate && <ErrorText>{errors.commissionExpirationDate}</ErrorText>}


          <Label>Upload Seal</Label>
          <Input type="file" name="seal" onChange={handleFileChange} />
          {errors.seal && <ErrorText>{errors.seal}</ErrorText>}

          <Label>Upload Signature</Label>
          <Input type="file" name="signature" onChange={handleFileChange} />
          {errors.signature && <ErrorText>{errors.signature}</ErrorText>}

          <Label>Upload Certificate</Label>
          <Input type="file" name="cert" onChange={handleFileChange} />
          {errors.cert && <ErrorText>{errors.cert}</ErrorText>}

          <SubmitButton
            onClick={handleSubmit}
            disabled={
              !formData.cert ||
              !formData.commissionExpirationDate ||
              !formData.county ||
              (!isLicensedUser && !formData.firm) ||
              !formData.number ||
              !formData.seal ||
              !formData.signature ||
              !formData.state ||
              !formData.market ||
              !formData.timezone ||
              !formData.user
            }
          >
            {isOnboarding ? <LoadingSpinner /> : "Onboard Official"}
          </SubmitButton>
        </Form>
      </SignInBox>
    </PageContainer>
  );
};

export default OnboardOfficial;

const SignInBox = styled.div`
  width: 50%;
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  // height: 100vh;
  background-size: cover;
  padding-bottom: 20px;
  padding-top: 20px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 20px;
`;

const Form = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: bold;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const Select = styled.select`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const UploadLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
`;

const UploadLink = styled.a`
  color: blue;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled.button`
  padding: 10px;
  border: none;
  background-color: #007bff;
  color: white;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 2px solid white;
  width: 12px;
  height: 12px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorText = styled.span`
  color: red;
  font-size: 14px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
